import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  getInvoiceTemplates,
  deleteInvoiceTemplate,
  setDefaultTemplate
} from '../services/invoiceTemplateService';

const InvoiceTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [ownershipFilter, setOwnershipFilter] = useState('all');
  const [companyNameFilter, setCompanyNameFilter] = useState('');
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    fetchTemplates();
  }, [filter, ownershipFilter]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filter !== 'all') {
        filters.type = filter;
      }
      if (ownershipFilter) {
        filters.ownership = ownershipFilter;
      }
      const data = await getInvoiceTemplates(filters);

      // Apply client-side company name filter if specified
      let filteredTemplates = data.templates || [];
      if (companyNameFilter) {
        const searchTerm = companyNameFilter.toLowerCase();
        filteredTemplates = filteredTemplates.filter(template => {
          const companyName = template.user?.companySettings?.companyName || '';
          return companyName.toLowerCase().includes(searchTerm);
        });
      }

      setTemplates(filteredTemplates);
      setError('');
    } catch (err) {
      setError(t('templates.failedToLoad'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(t('templates.deleteConfirm'))) {
      return;
    }

    try {
      await deleteInvoiceTemplate(id);
      setTemplates(templates.filter(t => t.id !== id));
    } catch (err) {
      alert(err.response?.data?.error || t('templates.failedToDelete'));
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await setDefaultTemplate(id);
      fetchTemplates();
    } catch (err) {
      alert(err.response?.data?.error || t('templates.failedToSetDefault'));
    }
  };

  const getTypeLabel = (type) => {
    return type === 'incoming' ? t('invoices.incoming') : t('invoices.outgoing');
  };

  const getTypeBadgeClass = (type) => {
    return type === 'incoming'
      ? 'bg-pastel-green-600 dark:bg-pastel-green-700 text-white'
      : 'bg-pastel-purple-600 dark:bg-pastel-purple-700 text-white';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-pastel-purple-500"></div>
          <p className="text-lg text-gray-700 dark:text-gray-300">{t('templates.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white bg-gradient-to-r from-pastel-purple-600 to-pastel-blue-600 bg-clip-text text-transparent">
          {t('templates.title')}
        </h1>
        <button
          className="px-6 py-2 bg-gradient-to-r from-pastel-purple-600 to-pastel-blue-600 hover:from-pastel-purple-700 hover:to-pastel-blue-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
          onClick={() => navigate('/invoice-templates/new')}
        >
          {t('templates.createTemplate')}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-200 px-6 py-4 rounded-xl mb-6">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.ownership')}</label>
          <select
            className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
            value={ownershipFilter}
            onChange={(e) => setOwnershipFilter(e.target.value)}
          >
            {user?.role === 'super_admin' ? (
              <>
                <option value="all">{t('invoices.allUsersData')}</option>
                <option value="mine">{t('templates.myTemplates')}</option>
              </>
            ) : (
              <>
                <option value="all">{t('invoices.allMineAndShared')}</option>
                <option value="mine">{t('templates.myTemplates')}</option>
                <option value="shared">{t('invoices.sharedWithMe')}</option>
              </>
            )}
          </select>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('invoices.type')}</label>
          <div className="flex gap-2">
            <button
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-pastel-purple-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
              onClick={() => setFilter('all')}
            >
              {t('invoices.all')}
            </button>
            <button
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'incoming'
                  ? 'bg-pastel-green-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
              onClick={() => setFilter('incoming')}
            >
              {t('invoices.incoming')}
            </button>
            <button
              className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === 'outgoing'
                  ? 'bg-pastel-purple-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
              onClick={() => setFilter('outgoing')}
            >
              {t('invoices.outgoing')}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('templates.companyName')}</label>
          <input
            type="text"
            className="w-full px-4 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-pastel-purple-500 focus:border-transparent text-gray-900 dark:text-gray-100"
            placeholder={t('invoices.searchByCompany')}
            value={companyNameFilter}
            onChange={(e) => {
              setCompanyNameFilter(e.target.value);
              setTimeout(() => fetchTemplates(), 300);
            }}
          />
        </div>
      </div>

      {/* Templates */}
      {templates.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl">
          <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">{t('templates.noTemplatesFound')}</p>
          <button
            className="px-6 py-3 bg-gradient-to-r from-pastel-purple-600 to-pastel-blue-600 hover:from-pastel-purple-700 hover:to-pastel-blue-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300"
            onClick={() => navigate('/invoice-templates/new')}
          >
            {t('templates.createFirstTemplate')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map((template) => {
            const isOwner = template.userId === user?.id;
            const hasEditPermission = template.sharedPermission === 'edit';
            const canEditItem = isOwner || user?.role === 'super_admin' || hasEditPermission;
            const companyName = template.user?.companySettings?.companyName || template.user?.username || 'N/A';

            return (
              <div
                key={template.id}
                className={`bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-xl rounded-2xl border ${
                  isOwner
                    ? 'border-pastel-purple-200 dark:border-gray-700'
                    : 'border-pastel-blue-200 dark:border-pastel-blue-800 bg-pastel-blue-50/50 dark:bg-pastel-blue-900/10'
                } p-6 hover:shadow-2xl transition-all duration-300`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    {template.name}
                    {!isOwner && (
                      <span className="inline-block px-2 py-1 rounded-full text-xs font-semibold bg-pastel-blue-600 dark:bg-pastel-blue-700 text-white">
                        {t('invoices.shared')}
                      </span>
                    )}
                  </h3>
                  <div className="flex gap-2 flex-wrap">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getTypeBadgeClass(template.type)}`}>
                      {getTypeLabel(template.type)}
                    </span>
                    {template.isDefault && (
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-yellow-500 dark:bg-yellow-600 text-gray-900">
                        {t('templates.default')}
                      </span>
                    )}
                    {!template.isActive && (
                      <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-600 dark:bg-gray-700 text-white">
                        {t('templates.inactive')}
                      </span>
                    )}
                  </div>
                </div>

                {template.description && (
                  <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">{template.description}</p>
                )}

                <div className="border-t border-b border-gray-200 dark:border-gray-700 py-4 mb-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{t('invoices.ownerCompany')}:</span>
                    <span className="text-gray-900 dark:text-gray-100">
                      {companyName}
                      {isOwner && <span className="ml-1 text-pastel-green-600 dark:text-pastel-green-400 font-bold">({t('invoices.you')})</span>}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{t('templates.numberFormat')}:</span>
                    <code className="text-xs bg-gray-100 dark:bg-gray-900 px-2 py-1 rounded text-gray-900 dark:text-gray-100">
                      {template.numberFormat}
                    </code>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{t('templates.prefix')}:</span>
                    <span className="text-gray-900 dark:text-gray-100">{template.numberPrefix || '-'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{t('templates.taxRate')}:</span>
                    <span className="text-gray-900 dark:text-gray-100">{template.taxRate}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{t('templates.paymentTerms')}:</span>
                    <span className="text-gray-900 dark:text-gray-100">{template.paymentTermsDays} {t('templates.days')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="font-medium text-gray-700 dark:text-gray-300">{t('templates.counterReset')}:</span>
                    <span className="text-gray-900 dark:text-gray-100">
                      {template.resetCounterMonthly ? t('templates.monthly') : template.resetCounterAnnually ? t('templates.annually') : t('templates.never')}
                    </span>
                  </div>
                </div>

                <div className="flex gap-2 flex-wrap">
                  {canEditItem ? (
                    <>
                      <button
                        className="px-3 py-1.5 text-sm bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors"
                        onClick={() => navigate(`/invoice-templates/edit/${template.id}`)}
                      >
                        {t('common.edit')}
                      </button>
                      {!template.isDefault && (
                        <button
                          className="px-3 py-1.5 text-sm bg-pastel-purple-600 hover:bg-pastel-purple-700 text-white font-medium rounded-lg transition-colors"
                          onClick={() => handleSetDefault(template.id)}
                        >
                          {t('templates.setAsDefault')}
                        </button>
                      )}
                      <button
                        className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors"
                        onClick={() => handleDelete(template.id, template.name)}
                      >
                        {t('common.delete')}
                      </button>
                    </>
                  ) : (
                    <div className="text-sm text-gray-600 dark:text-gray-300 dark:text-gray-400 py-2">
                      {t('templates.viewOnly')}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InvoiceTemplates;
